import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { OfferCard } from "@sill/shared/components/marketplace/offer-card";

import type { ProBrowseCard } from "../../lib/marketplace-discovery-api";
import { AgentAvatar } from "./AgentAvatar";
import { toCardSavings } from "../../lib/offers-display";

export function YourAgentCard({
  agent,
  href,
  onClick,
}: {
  agent: ProBrowseCard;
  href: string;
  onClick?: () => void;
}) {
  const name = agent.identity.display_name ?? "Your agent";


  return (
    <Link
      className="marketplace-surface-interactive group relative block min-h-11 overflow-hidden p-4 pt-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid="your-agent-card"
      onClick={onClick}
      to={href}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-1 bg-primary"
      />
      <div className="flex items-start gap-3">
        <AgentAvatar name={name} photoUrl={agent.identity.photo_url} imageSize={88}
          className="size-11 shrink-0 rounded-full" fallbackClassName="bg-primary text-sm font-bold text-primary-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-wrap-balance font-semibold text-foreground">
              {name}
            </h3>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-primary">
              Referral agent
            </span>
          </div>
          {agent.identity.brokerage && (
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {agent.identity.brokerage}
            </p>
          )}
          <p className="mt-2 text-wrap-pretty text-sm font-medium text-foreground">
            Your referral stays active while you browse
          </p>
          {agent.distance_label && (
            <p className="mt-1 text-xs text-muted-foreground">
              {agent.distance_label}
            </p>
          )}
        </div>
        <span
          aria-hidden="true"
          className="mt-2 text-muted-foreground transition-transform group-hover:translate-x-0.5"
        >
          <ArrowRight className="size-4" />
        </span>
      </div>
      {agent.standing_offer && (
        <div
          className="mt-3 border-t border-border pt-3"
          data-testid="your-agent-standing-offer"
        >
          <OfferCard
            data={{
              commissionPct: agent.standing_offer.commission_pct,
              chrome: "own-agent",
              deliveryState: agent.standing_offer.delivery_state,
              // The browse card's estimate is computed from this same
              // standing offer (discovery.Browse).
              estimatedSavings: toCardSavings(agent.estimated_savings),
              items: agent.standing_offer.items,
              note: agent.standing_offer.note,
              proDisplayName: name,
              proPhotoUrl: agent.identity.photo_url,
              standing: true,
            }}
          />
        </div>
      )}
    </Link>
  );
}
